import { Link } from "react-router-dom";
import { Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Button } from "../ui/Button";
import { Badge } from "../ui/Badge";
import { type NewsListItem, type NewsArticle } from "../../lib/sanity";
import { buildNewsCardImageProps } from "../../lib/sanity-image-urls";

interface NewsCardProps {
  article: NewsListItem | NewsArticle;
}

export function NewsCard({ article }: NewsCardProps) {
  const categories = article.categories ?? [];
  const cardImage = article.image
    ? buildNewsCardImageProps(article.image)
    : null;

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full p-0 group">
      <div className="relative flex-shrink-0">
        <Link to={`/news/${article.slug.current}`} className="block">
          {cardImage ? (
            <img
              src={cardImage.src}
              srcSet={cardImage.srcSet}
              sizes={cardImage.sizes}
              width={cardImage.width}
              height={cardImage.height}
              alt={article.title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
              decoding="async"
            />
          ) : (
            <div className="w-full h-48 flex items-center justify-center bg-gray-100 text-gray-400">
              No image
            </div>
          )}
        </Link>
        {categories.length > 0 && (
          <div className="absolute bottom-2 left-2 flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <Badge
                key={cat.slug.current}
                variant="default"
                size="sm"
                className="bg-white text-gray-800"
              >
                {cat.name}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <CardHeader className="px-6 pt-6">
        <CardTitle className="text-xl line-clamp-2 group-hover:text-primary-600 transition-colors">
          <Link to={`/news/${article.slug.current}`}>{article.title}</Link>
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4 px-6 pb-6">
        <ul className="list-none space-y-2 text-sm text-gray-600 p-0 m-0">
          <li className="flex items-center gap-2">
            <Calendar className="w-4 h-4 flex-shrink-0 text-gray-500" />
            <span>{format(new Date(article.date), "MMM d, yyyy")}</span>
          </li>
          {article.author?.name && (
            <li className="flex items-center gap-2">
              <User className="w-4 h-4 flex-shrink-0 text-gray-500" />
              <span>{article.author.name}</span>
            </li>
          )}
        </ul>

        <div className="pt-4 mt-auto">
          <Button
            asChild
            className="h-auto min-h-10 py-2.5 whitespace-normal text-center"
          >
            <Link to={`/news/${article.slug.current}`}>
              Read more: {article.title}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
